import React, { useCallback, useEffect } from 'react';
import { JiraSoftwareBoardAri } from '@atlassian/ari/jira-software/board';
import { useBoardId } from '@atlassian/jira-board-settings-common/src/common/utils/use-board-id';
import { useEntryPoint } from '@atlassian/jira-entry-point';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container';
import { JSErrorPageAsync } from '@atlassian/jira-error-pages';
import { BoardSettingsEstimatesSkeleton } from '@atlassian/jira-skeletons';
import { useOnce } from '@atlassian/jira-software-board-settings-rapidboard-config-common';
import { useCloudId } from '@atlassian/jira-tenant-context-controller';
import { APP_BOUNDARY_ID, APP_SOURCE_NAME, APP_TEAM_NAME } from './common/constants';
import { estimationPageEntryPoint } from './entrypoint';
import { useViewEstimationSettingsExperienceTracker } from './services';

export const EstimationSettingsPage = () => {
	const boardId = useBoardId();
	const cloudId = useCloudId();

	const boardAri = JiraSoftwareBoardAri.create({
		siteId: cloudId,
		boardId: String(boardId),
	}).toString();

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		estimationPageEntryPoint,
		{ boardId: boardAri, cloudId },
	);

	const { fireExperienceStart, fireExperienceError } = useViewEstimationSettingsExperienceTracker({
		isClassic: true,
	});

	const errorFallback = useCallback(
		({ error }: { error: Error }) => {
			fireExperienceError(error);

			return <JSErrorPageAsync error={error} />;
		},
		[fireExperienceError],
	);

	useOnce(fireExperienceStart);

	useEffect(() => {
		entryPointActions.load();
		return () => {
			entryPointActions.unload();
		};
	}, [entryPointActions]);

	return (
		<JiraEntryPointContainer
			id={APP_BOUNDARY_ID}
			packageName={APP_SOURCE_NAME}
			teamName={APP_TEAM_NAME}
			entryPointReferenceSubject={entryPointReferenceSubject}
			runtimeProps={{}}
			fallback={<BoardSettingsEstimatesSkeleton />}
			errorFallback={errorFallback}
		/>
	);
};
